/* Resources — the official knowledge center. Server-side full-text search,
   category-grouped official guides, Stripe/Notion-grade presentation. */
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  CreditCard,
  Gavel,
  Globe,
  HelpCircle,
  Rocket,
  Scale,
  Search,
  SearchX,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { BRAND } from "@/config/brand";

const CAT_ICONS = {
  rocket: Rocket,
  gavel: Gavel,
  horse: BookOpen,
  "credit-card": CreditCard,
  globe: Globe,
  scale: Scale,
  users: Users,
  help: HelpCircle,
};

const AudienceChip = ({ audience }) => {
  const { t } = useTranslation();
  const key = ["buyer", "organizer"].includes(audience) ? audience : "all";
  return (
    <span className="inline-flex items-center rounded-full bg-black/[0.04] px-2.5 py-0.5 text-[11px] font-medium text-[#0A0A0A]/55">
      {t(`kb.audience.${key}`)}
    </span>
  );
};

const ArticleCard = ({ article }) => {
  const external = Boolean(article.external_path);
  const Inner = (
    <>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[15px] font-semibold tracking-tight text-[#0A0A0A] group-hover:text-[#1D4D3B] transition-colors duration-200">
          {article.title}
        </h3>
        {external ? (
          <ArrowUpRight className="w-4 h-4 text-[#0A0A0A]/25 group-hover:text-[#1D4D3B] transition-colors duration-200 shrink-0 mt-0.5" aria-hidden="true" />
        ) : (
          <ArrowRight className="w-4 h-4 text-[#0A0A0A]/0 group-hover:text-[#1D4D3B] transition-all duration-200 shrink-0 mt-0.5 -translate-x-1 group-hover:translate-x-0" aria-hidden="true" />
        )}
      </div>
      <p className="text-sm text-[#737373] leading-relaxed line-clamp-2">{article.subtitle}</p>
      <div className="mt-auto pt-3">
        <AudienceChip audience={article.audience} />
      </div>
    </>
  );
  const cls =
    "group flex flex-col gap-2 rounded-2xl border border-black/5 bg-white p-6 hover:border-black/15 hover:shadow-[0_8px_24px_rgb(0,0,0,0.05)] transition-all duration-200";
  return external ? (
    <Link to={article.external_path} data-testid={`resource-card-${article.slug}`} className={cls}>
      {Inner}
    </Link>
  ) : (
    <Link to={`/resources/${article.slug}`} data-testid={`resource-card-${article.slug}`} className={cls}>
      {Inner}
    </Link>
  );
};

const SearchResult = ({ result }) => {
  const { t } = useTranslation();
  const hash = result.snippet?.section_id ? `#${result.snippet.section_id}` : "";
  const to = result.external_path || `/resources/${result.slug}${hash}`;
  return (
    <Link
      to={to}
      data-testid={`resource-result-${result.slug}`}
      className="group flex flex-col gap-1.5 rounded-2xl border border-black/5 bg-white p-6 hover:border-black/15 transition-colors duration-200"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-[15px] font-semibold tracking-tight text-[#0A0A0A] group-hover:text-[#1D4D3B] transition-colors duration-200">
          {result.title}
        </h3>
        <AudienceChip audience={result.audience} />
      </div>
      {result.snippet && (
        <>
          <p className="text-xs font-medium text-[#1D4D3B]">{t("kb.inSection", { section: result.snippet.section_title })}</p>
          <p className="text-sm text-[#737373] leading-relaxed line-clamp-2">{result.snippet.text}</p>
        </>
      )}
      {!result.snippet && <p className="text-sm text-[#737373] leading-relaxed line-clamp-2">{result.subtitle}</p>}
    </Link>
  );
};

export default function ResourcesPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [audience, setAudience] = useState("all");
  const debounceRef = useRef(null);

  usePageMeta({ title: `${t("kb.title")} — ${BRAND.name}`, description: t("kb.heroSubtitle"), localized: true });

  useEffect(() => {
    api
      .get("/resources/articles", { params: { locale: i18n.resolvedLanguage } })
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData({ categories: [], articles: [] }));
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      api
        .get("/resources/search", { params: { q, locale: i18n.resolvedLanguage } })
        .then(({ data: payload }) => setResults(payload.results))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, i18n.resolvedLanguage]);

  const matchesAudience = (a) => audience === "all" || a.audience === audience || !["buyer", "organizer"].includes(a.audience);
  const searching = query.trim().length >= 2;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 pb-24" data-testid="resources-page">
      {/* hero + search */}
      <header className="pt-16 pb-12 flex flex-col gap-6 max-w-2xl">
        <div className="flex flex-col gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1D4D3B]">{t("kb.eyebrow")}</p>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-[#0A0A0A]">{t("kb.heroTitle")}</h1>
          <p className="text-base text-[#737373] leading-relaxed">{t("kb.heroSubtitle")}</p>
        </div>
        <div className="relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-[#0A0A0A]/35" aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("kb.searchPlaceholder")}
            aria-label={t("kb.searchPlaceholder")}
            data-testid="resources-search-input"
            className="w-full h-14 pr-12 rounded-full border border-black/10 bg-white text-[15px] text-[#0A0A0A] placeholder:text-[#0A0A0A]/35 focus:outline-none focus:border-[#1D4D3B] focus:ring-4 focus:ring-[#1D4D3B]/10 transition-all duration-200"
            style={{ paddingLeft: "3.25rem" }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label={t("kb.clear")}
              data-testid="resources-search-clear"
              className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center transition-colors duration-200"
            >
              <X className="w-4 h-4 text-[#0A0A0A]/50" aria-hidden="true" />
            </button>
          )}
        </div>
        {!searching && data && (
          <div className="flex gap-2" role="group" aria-label={t("kb.filterAll")}>
            {[
              ["all", t("kb.filterAll")],
              ["buyer", t("kb.filterBuyers")],
              ["organizer", t("kb.filterOrganizers")],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`resources-filter-${key}`}
                onClick={() => setAudience(key)}
                aria-pressed={audience === key}
                className={`h-9 px-4 rounded-full text-[13px] font-medium transition-colors duration-200 ${
                  audience === key ? "bg-[#0A0A0A] text-white" : "bg-black/[0.04] text-[#0A0A0A]/60 hover:bg-black/[0.08]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* loading */}
      {!data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resources-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
      )}

      {/* search results */}
      {data && searching && (
        <section className="flex flex-col gap-4" data-testid="resources-results">
          <h2 className="text-sm font-semibold text-[#0A0A0A]/60">
            {results === null ? "…" : t("kb.resultsFor", { query: query.trim() })}
          </h2>
          {results !== null && results.length === 0 && (
            <EmptyState
              icon={SearchX}
              testid="resources-search-empty"
              title={t("kb.searchEmptyTitle", { query: query.trim() })}
              description={t("kb.searchEmptyDesc")}
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(results || []).map((r) => (
              <SearchResult key={r.slug} result={r} />
            ))}
          </div>
        </section>
      )}

      {/* category directory */}
      {data && !searching && (
        <div className="flex flex-col gap-14">
          {data.categories.map((cat) => {
            const articles = data.articles.filter((a) => a.category === cat.key && matchesAudience(a));
            if (!articles.length) return null;
            const Icon = CAT_ICONS[cat.icon] || BookOpen;
            return (
              <section key={cat.key} className="flex flex-col gap-5" data-testid={`resources-category-${cat.key}`}>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-[#1D4D3B]/5 border border-[#1D4D3B]/10 flex items-center justify-center shrink-0" aria-hidden="true">
                    <Icon className="w-4 h-4 text-[#1D4D3B]" />
                  </span>
                  <h2 className="text-xl font-medium tracking-tight text-[#0A0A0A]">{t(`kb.cat.${cat.key}`)}</h2>
                  <span className="text-xs text-[#737373] tabular-nums">{articles.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {articles.map((a) => (
                    <ArticleCard key={a.slug} article={a} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
